import {
  getWebInstrumentationsRum,
  initializeRum,
  OtlpHttpTransportRum,
  TracingInstrumentationRum,
} from './index.js';
import { ensureApi, loadEnabledFeatures } from './feature-loader.js';
import {
  emptyHostEntry,
  fetchManifest,
  resolveHostForHostname,
} from './manifest.js';
import { getRoot, getSdkScriptElement, parseScriptUrl, variable_constants } from './url.js';

const api = ensureApi();
Object.assign(api, {
  getWebInstrumentationsRum,
  initializeRum,
  OtlpHttpTransportRum,
  TracingInstrumentationRum,
});

/**
 * Attach resolved host features/config onto the public API.
 * @param {{ features?: Record<string, boolean>, config?: Record<string, unknown> }} entry
 */
function attachHostEntry(entry) {
  const resolved = entry || emptyHostEntry();
  api.features = { ...(resolved.features || {}) };
  api.config = {
    rum: { ...((resolved.config && resolved.config.rum) || {}) },
  };
  // Only expose record config when the record feature is enabled
  if (resolved.features && resolved.features.record === true) {
    api.config.record = {
      ...((resolved.config && resolved.config.record) || {}),
    };
  } else if (api.config && 'record' in api.config) {
    delete api.config.record;
  }
  // Adding Shopify specific rum endpoints
  api.url_constants = variable_constants;
}

/**
 * Dispatch CstPrumReady after core (+ optional features) are ready.
 * @param {Record<string, unknown>} detail
 */
function dispatchReady(detail) {
  const root = getRoot();
  if (!root) return;

  root.CstPrum = detail;

  try {
    const event = new CustomEvent('CstPrumReady', { detail });
    (root.document || root).dispatchEvent(event);
  } catch {
    // CustomEvent may be unavailable in some environments
  }
}

/**
 * Bootstrap:
 *   cst-prum.iife.min.js?client=cl-1
 *   → fetch clients/cl-1/manifest.json
 *   → pick features + config for location.hostname
 *   → load enabled ./features/*.iife.min.js
 *   → attach api.features / api.config
 *   → CstPrumReady
 */
async function bootstrap() {
  const scriptEl = getSdkScriptElement();
  const scriptUrl = scriptEl && scriptEl.src ? scriptEl.src : '';
  const { clientId, baseUrl } = parseScriptUrl(scriptUrl);

  if (baseUrl && clientId) {
    const manifestUrl = new URL(`clients/${encodeURIComponent(clientId)}/manifest.json`, baseUrl)
      .href;
    const manifest = await fetchManifest(manifestUrl);
    const hostEntry = resolveHostForHostname(manifest);
    attachHostEntry(hostEntry);
    await loadEnabledFeatures(hostEntry.features, baseUrl);
  } else {
    attachHostEntry(emptyHostEntry());
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(
        '[CstPrum] Missing ?client= on script URL; loading core API only (no features).',
      );
    }
  }

  dispatchReady(api);
}

bootstrap().catch((err) => {
  if (typeof console !== 'undefined' && console.warn) {
    console.warn('[CstPrum] Bootstrap failed; continuing with core API only:', err);
  }
  attachHostEntry(emptyHostEntry());
  dispatchReady(api);
});
